import type { ReactNode } from 'react';
import type { Period } from '@/lib/finance';

// ── Carte statistique ─────────────────────────────────────────
export function FinanceStatCard({
  label,
  value,
  icon,
  tone = 'primary',
  hint,
}: {
  label: string;
  value: string;
  icon: string;
  tone?: 'primary' | 'accent' | 'secondary' | 'danger';
  hint?: string;
}) {
  const tones: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-700',
    danger: 'bg-red-100 text-red-600',
  };
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-foreground-500">{label}</span>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <i className={`${icon} text-base`}></i>
        </span>
      </div>
      <div className="text-lg md:text-xl font-bold font-heading text-foreground-950 truncate">{value}</div>
      {hint && <div className="text-[11px] text-foreground-400 mt-0.5">{hint}</div>}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────
export function FinanceSection({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          {icon && <i className={`${icon} text-primary-500`}></i>}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

// ── État vide ─────────────────────────────────────────────────
export function FinanceEmpty({
  icon,
  title,
  hint,
  action,
}: {
  icon: string;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
        <i className={`${icon} text-2xl text-foreground-400`}></i>
      </div>
      <p className="text-sm font-medium text-foreground-700">{title}</p>
      {hint && <p className="text-xs text-foreground-400 mt-1 max-w-xs">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Filtre de période ─────────────────────────────────────────
const PERIODS: { key: Period; label: string }[] = [
  { key: 'today', label: "Aujourd'hui" },
  { key: 'week', label: 'Cette semaine' },
  { key: 'month', label: 'Ce mois' },
  { key: 'year', label: 'Cette année' },
  { key: 'custom', label: 'Personnalisé' },
];

export function PeriodFilter({
  value,
  onChange,
  customStart,
  customEnd,
  onCustomChange,
}: {
  value: Period;
  onChange: (p: Period) => void;
  customStart?: string;
  customEnd?: string;
  onCustomChange?: (start: string, end: string) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto max-w-full">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              value === p.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {value === 'custom' && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={customStart}
            onChange={(e) => onCustomChange?.(e.target.value, customEnd || '')}
            className="px-3 py-1.5 rounded-lg text-xs bg-background-50 border border-background-200/70 text-foreground-800 focus:outline-none focus:border-primary-300"
          />
          <span className="text-xs text-foreground-400">→</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => onCustomChange?.(customStart || '', e.target.value)}
            className="px-3 py-1.5 rounded-lg text-xs bg-background-50 border border-background-200/70 text-foreground-800 focus:outline-none focus:border-primary-300"
          />
        </div>
      )}
    </div>
  );
}

// ── Modal générique ───────────────────────────────────────────
export function FinanceModal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/30 animate-fade-in" onClick={onClose}></div>
      <div
        className={`relative bg-background-50 rounded-t-2xl sm:rounded-2xl w-full ${
          wide ? 'sm:max-w-2xl' : 'sm:max-w-md'
        } mx-0 sm:mx-4 max-h-[90vh] flex flex-col animate-scale-in`}
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground-950">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

// ── Champ de formulaire ───────────────────────────────────────
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs font-semibold text-foreground-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

export const inputClass =
  'w-full px-3 py-2 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors';